"use client";

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { API_BASE_URL } from "@lib/config";

type AnalyticsItem = {
  item_id: string;
  item_name: string;
  content_id?: string;
  product_id?: string;
  variant_id?: string;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
};

type CommerceEventPayload = {
  items: AnalyticsItem[];
  currency?: string;
  value?: number;
  coupon?: string | null;
  transactionId?: string;
  eventId?: string;
  eventSourceUrl?: string;
};

type MetaPixelEvent = {
  method: "track" | "trackCustom";
  name: string;
  params?: Record<string, unknown>;
  eventId?: string;
};

const sanitizeCurrency = (currency?: string) => {
  if (typeof currency !== "string") return undefined;
  const trimmed = currency.trim();
  return trimmed ? trimmed.toUpperCase() : undefined;
};

const sanitizeAmount = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Number(value.toFixed(2));
};

const buildMetaPixelCommerceParams = (payload: CommerceEventPayload): Record<string, unknown> => {
  const params: Record<string, unknown> = {};
  const contentIds = payload.items
    .map((item) => item.content_id || item.item_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  if (contentIds.length > 0) {
    params.content_ids = Array.from(new Set(contentIds));
  }
  const contents = payload.items
    .map((item) => {
      const entry: Record<string, unknown> = {};
      const id = item.content_id || item.item_id;
      if (id) {
        entry.id = id;
      }
      if (typeof item.quantity === "number" && Number.isFinite(item.quantity)) {
        entry.quantity = item.quantity;
      }
      if (typeof item.price === "number" && Number.isFinite(item.price)) {
        entry.item_price = sanitizeAmount(item.price);
      }
      return entry;
    })
    .filter((entry) => Object.keys(entry).length > 0);
  if (contents.length > 0) {
    params.contents = contents;
    params.num_items = contents.reduce((sum, item) => {
      const quantity = Number(item.quantity || 0);
      return sum + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  }
  const value = sanitizeAmount(payload.value);
  if (typeof value === "number") {
    params.value = value;
  }
  const currency = sanitizeCurrency(payload.currency);
  if (currency) {
    params.currency = currency;
  }
  const firstNamedItem = payload.items.find((item) => Boolean(item.item_name));
  if (firstNamedItem?.item_name) {
    params.content_name = firstNamedItem.item_name;
  }
  const firstCategorizedItem = payload.items.find((item) => Boolean(item.item_category));
  if (firstCategorizedItem?.item_category) {
    params.content_category = firstCategorizedItem.item_category;
  }
  return params;
};

const mapCommerceEventToMetaPixel = (
  eventName: string,
  payload: CommerceEventPayload
): MetaPixelEvent | null => {
  const baseParams = buildMetaPixelCommerceParams(payload);

  switch (eventName) {
    case "view_item":
      return {
        method: "track",
        name: "ViewContent",
        params: { ...baseParams, content_type: "product" },
      };
    case "view_cart":
      return {
        method: "track",
        name: "ViewContent",
        params: {
          ...baseParams,
          content_type: "product_group",
          content_name: (baseParams.content_name as string | undefined) || "Cart",
        },
      };
    case "add_to_cart":
      return {
        method: "track",
        name: "AddToCart",
        params: { ...baseParams, content_type: "product" },
      };
    case "remove_from_cart":
      return {
        method: "track",
        name: "RemoveFromCart",
        params: { ...baseParams, content_type: "product" },
      };
    case "begin_checkout":
      return {
        method: "track",
        name: "InitiateCheckout",
        params: { ...baseParams, content_type: "product" },
      };
    case "purchase": {
      const params: Record<string, unknown> = { ...baseParams, content_type: "product" };
      if (payload.transactionId) {
        params.order_id = payload.transactionId;
      }
      if (payload.coupon) {
        params.coupon = payload.coupon;
      }
      return {
        method: "track",
        name: "Purchase",
        params,
        eventId: payload.eventId,
      };
    }
    case "cart_abandonment":
      return {
        method: "trackCustom",
        name: "CartAbandonment",
        params: { ...baseParams, content_type: "product" },
      };
    default:
      return null;
  }
};

type AnalyticsContextValue = {
  measurementId?: string;
  ready: boolean;
  gaReady: boolean;
  metaPixelReady: boolean;
  metaConversionsApiReady: boolean;
  trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
  trackViewItem: (payload: CommerceEventPayload) => void;
  trackViewCart: (payload: CommerceEventPayload) => void;
  trackAddToCart: (payload: CommerceEventPayload) => void;
  trackRemoveFromCart: (payload: CommerceEventPayload) => void;
  trackBeginCheckout: (payload: CommerceEventPayload) => void;
  trackPurchase: (payload: CommerceEventPayload) => void;
  trackCheckoutAbandonment: (payload: CommerceEventPayload) => void;
};

const noop = () => {};

const AnalyticsContext = createContext<AnalyticsContextValue>({
  ready: false,
  gaReady: false,
  metaPixelReady: false,
  metaConversionsApiReady: false,
  trackEvent: noop,
  trackViewItem: noop,
  trackViewCart: noop,
  trackAddToCart: noop,
  trackRemoveFromCart: noop,
  trackBeginCheckout: noop,
  trackPurchase: noop,
  trackCheckoutAbandonment: noop,
});

const sendGaEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window === "undefined") {
    return;
  }
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") {
    return;
  }
  try {
    gtag("event", eventName, params);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Analytics event failed", { eventName, error });
    }
  }
};

const getBrowserCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : null;
};

const resolvePurchaseEventId = (payload: CommerceEventPayload): string | null => {
  if (typeof payload.eventId === "string" && payload.eventId.trim()) {
    return payload.eventId.trim();
  }
  if (typeof payload.transactionId === "string" && payload.transactionId.trim()) {
    return `lealti_purchase_${payload.transactionId.trim()}`;
  }
  return null;
};

const buildProviderEventStorageKey = (
  provider: "ga" | "meta_pixel" | "meta_capi",
  eventName: string,
  eventId: string
) => `analytics_${provider}_${eventName}_${eventId}`;

const hasSentBrowserEvent = (
  provider: "ga" | "meta_pixel" | "meta_capi",
  eventName: string,
  eventId?: string | null
): boolean => {
  if (!eventId || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(
      buildProviderEventStorageKey(provider, eventName, eventId)
    ) === "1";
  } catch {
    return false;
  }
};

const markBrowserEventSent = (
  provider: "ga" | "meta_pixel" | "meta_capi",
  eventName: string,
  eventId?: string | null
) => {
  if (!eventId || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      buildProviderEventStorageKey(provider, eventName, eventId),
      "1"
    );
  } catch {
    // ignore storage access issues
  }
};

const metaCapiInFlightEvents = new Set<string>();

const sendMetaCapiPurchase = (payload: CommerceEventPayload) => {
  if (typeof window === "undefined") return;
  const orderId =
    typeof payload.transactionId === "string" ? payload.transactionId.trim() : "";
  const eventId = resolvePurchaseEventId(payload);
  if (!orderId || !eventId) return;

  if (
    hasSentBrowserEvent("meta_capi", "purchase", eventId) ||
    metaCapiInFlightEvents.has(eventId)
  ) {
    return;
  }
  metaCapiInFlightEvents.add(eventId);

  const eventSourceUrl = payload.eventSourceUrl || window.location.href;
  void fetch(`${API_BASE_URL}/public/analytics/meta/capi/purchase`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      orderId,
      eventId,
      eventSourceUrl,
      fbp: getBrowserCookie("_fbp"),
      fbc: getBrowserCookie("_fbc"),
    }),
  })
    .then((response) => {
      if (!response.ok) return;
      markBrowserEventSent("meta_capi", "purchase", eventId);
    })
    .catch((error) => {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Meta CAPI purchase event failed", error);
      }
    })
    .finally(() => {
      metaCapiInFlightEvents.delete(eventId);
    });
};

export function AnalyticsProvider({
  measurementId,
  metaPixelId,
  metaConversionsApiEnabled,
  children,
}: {
  measurementId?: string | null;
  metaPixelId?: string | null;
  metaConversionsApiEnabled?: boolean | null;
  children: React.ReactNode;
}) {
  const normalizedId = typeof measurementId === "string" && measurementId.trim()
    ? measurementId.trim()
    : undefined;
  const pixelId = typeof metaPixelId === "string" && metaPixelId.trim() ? metaPixelId.trim() : undefined;

  const gaMeasurementId = normalizedId;
  const metaCapiActive = Boolean(metaConversionsApiEnabled);
  const [metaPixelReady, setMetaPixelReady] = useState(false);
  const initializedPixelsRef = useRef<Set<string>>(new Set());
  const hasTrackedInitialPageView = useRef(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pixelId) {
      setMetaPixelReady(false);
      hasTrackedInitialPageView.current = false;
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const ensurePixel = () => {
      const fbq = (window as typeof window & { fbq?: (...args: unknown[]) => void }).fbq;
      if (typeof fbq !== "function") {
        return false;
      }
      if (!initializedPixelsRef.current.has(pixelId)) {
        fbq("init", pixelId);
        initializedPixelsRef.current.add(pixelId);
      }
      if (!hasTrackedInitialPageView.current) {
        fbq("track", "PageView");
        hasTrackedInitialPageView.current = true;
      }
      setMetaPixelReady(true);
      return true;
    };

    if (ensurePixel()) {
      return;
    }

    const interval = window.setInterval(() => {
      if (ensurePixel()) {
        window.clearInterval(interval);
      }
    }, 250);

    return () => window.clearInterval(interval);
  }, [pixelId]);

  const searchString = searchParams?.toString() ?? "";
  const resolvedPath = useMemo(() => {
    const path = pathname || "/";
    return searchString ? `${path}?${searchString}` : path;
  }, [pathname, searchString]);

  useEffect(() => {
    if (!pixelId || !metaPixelReady || !hasTrackedInitialPageView.current) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const fbq = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof fbq === "function") {
      fbq("track", "PageView");
    }
  }, [resolvedPath, pixelId, metaPixelReady]);

  useEffect(() => {
    if (!gaMeasurementId) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const gtag = (window as typeof window & { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== "function") {
      return;
    }
    gtag("config", gaMeasurementId, {
      page_path: resolvedPath,
    });
  }, [gaMeasurementId, resolvedPath]);

  const metaActive = Boolean(pixelId) && metaPixelReady;

  const value = useMemo<AnalyticsContextValue>(() => {
    if (!gaMeasurementId && !metaActive && !metaCapiActive) {
      return {
        measurementId: undefined,
        ready: false,
        gaReady: false,
        metaPixelReady: false,
        metaConversionsApiReady: false,
        trackEvent: noop,
        trackViewItem: noop,
        trackViewCart: noop,
        trackAddToCart: noop,
        trackRemoveFromCart: noop,
        trackBeginCheckout: noop,
        trackPurchase: noop,
        trackCheckoutAbandonment: noop,
      };
    }

    const trackGa = gaMeasurementId
      ? (eventName: string, params?: Record<string, unknown>) => {
          sendGaEvent(eventName, params);
        }
      : noop;

    const trackMeta = metaActive
      ? (
          method: MetaPixelEvent["method"],
          name: string,
          params?: Record<string, unknown>,
          eventId?: string
        ) => {
          if (typeof window === "undefined") {
            return;
          }
          const fbq = (window as Window & { fbq?: (...args: unknown[]) => void }).fbq;
          if (typeof fbq !== "function") {
            return;
          }
          try {
            if (eventId) {
              fbq(method, name, params, { eventID: eventId });
            } else {
              fbq(method, name, params);
            }
          } catch (error) {
            if (process.env.NODE_ENV !== "production") {
              console.warn("Meta Pixel event failed", { method, name, error });
            }
          }
        }
      : noop;

    const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
      trackGa(eventName, params);
      if (metaActive) {
        trackMeta("trackCustom", eventName, params);
      }
    };

    const trackCommerceEvent = (eventName: string, payload: CommerceEventPayload) => {
      const purchaseEventId =
        eventName === "purchase" ? resolvePurchaseEventId(payload) : payload.eventId || null;
      const payloadWithEventId =
        purchaseEventId && !payload.eventId
          ? { ...payload, eventId: purchaseEventId }
          : payload;
      const sanitized = {
        ...payloadWithEventId,
        value: typeof payload.value === "number" ? Number(payload.value.toFixed(2)) : undefined,
        coupon: payload.coupon || undefined,
        transaction_id: payload.transactionId,
        event_id: purchaseEventId || undefined,
      } as Record<string, unknown>;
      delete sanitized.transactionId;

      if (
        eventName !== "purchase" ||
        !purchaseEventId ||
        !hasSentBrowserEvent("ga", eventName, purchaseEventId)
      ) {
        trackGa(eventName, sanitized);
        if (gaMeasurementId && eventName === "purchase") {
          markBrowserEventSent("ga", eventName, purchaseEventId);
        }
      }

      if (metaActive) {
        const mapped = mapCommerceEventToMetaPixel(eventName, payloadWithEventId);
        if (mapped) {
          if (
            eventName !== "purchase" ||
            !purchaseEventId ||
            !hasSentBrowserEvent("meta_pixel", eventName, purchaseEventId)
          ) {
            trackMeta(mapped.method, mapped.name, mapped.params, mapped.eventId);
            if (eventName === "purchase") {
              markBrowserEventSent("meta_pixel", eventName, purchaseEventId);
            }
          }
        }
      }
      if (eventName === "purchase" && metaCapiActive) {
        sendMetaCapiPurchase(payloadWithEventId);
      }
    };

    return {
      measurementId: gaMeasurementId,
      ready: Boolean(gaMeasurementId) || metaActive || metaCapiActive,
      gaReady: Boolean(gaMeasurementId),
      metaPixelReady: metaActive,
      metaConversionsApiReady: metaCapiActive,
      trackEvent,
      trackViewItem: (payload) => trackCommerceEvent("view_item", payload),
      trackViewCart: (payload) => trackCommerceEvent("view_cart", payload),
      trackAddToCart: (payload) => trackCommerceEvent("add_to_cart", payload),
      trackRemoveFromCart: (payload) => trackCommerceEvent("remove_from_cart", payload),
      trackBeginCheckout: (payload) => trackCommerceEvent("begin_checkout", payload),
      trackPurchase: (payload) => trackCommerceEvent("purchase", payload),
      trackCheckoutAbandonment: (payload) => trackCommerceEvent("cart_abandonment", payload),
    };
  }, [gaMeasurementId, metaActive, metaCapiActive]);

  return (
    <AnalyticsContext.Provider value={value}>
      {gaMeasurementId ? (
        <>
          <Script
            id="ga-loader"
            strategy="lazyOnload"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
          />
          <Script id="ga-init" strategy="lazyOnload">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gaMeasurementId}', { anonymize_ip: true, allow_google_signals: false });
            `}
          </Script>
        </>
      ) : null}
      {pixelId ? (
        <Script id="fb-pixel-bootstrap" strategy="lazyOnload">
          {`
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
          `}
        </Script>
      ) : null}
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

export type { AnalyticsItem, CommerceEventPayload };
